import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {useAccountStatuses} from 'src/hooks/dictionaries';
import {getAccountStatusBulkUpdateResult, getBulkAccountsStatusesProgress} from 'src/openapi/admin/apis/AccountStatusBulkUpdateApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UpdateAccountProcessedItem} from 'src/openapi/admin/models/UpdateAccountProcessedItem';

type Props = {
    id: string,
    onClose: () => void,
};

const MODAL_WIDTH = 913;

export const UpdateAccountStatusResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();
    const {getAccountStatusByCode} = useAccountStatuses();

    const getPaginatedResults = useCallback(
        (bulkResult: UpdateAccountProcessedItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UpdateAccountProcessedItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <AccountLink
                            accountId={res.accountId || 0}
                            accountName={res.accountName}
                            blank={true}
                            place="Bulk Account Status Update Result"
                        />
                    </Cell>
                    <Cell>{getAccountStatusByCode(res.oldStatus)?.description}</Cell>
                    <Cell>{getAccountStatusByCode(res.newStatus)?.description}</Cell>
                    <Cell><CompleteMark errors={res.errors}/></Cell>
                </Row>
            )),
        [getAccountStatusByCode],
    );

    return (
        <BulkActionResultModal<UpdateAccountProcessedItem>
            id={id}
            onClose={onClose}
            width={MODAL_WIDTH}
            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            accountId: item.accountId.toString(),
                            oldStatus: getAccountStatusByCode(item.oldStatus)?.description,
                            newStatus: getAccountStatusByCode(item.newStatus)?.description,
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={246}>Account Name</Cell>
                        <Cell width={200}>Old Status</Cell>
                        <Cell width={200}>New Status</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getBulkAccountsStatusesProgress({id}))}
            getResult={() => apiDispatch(getAccountStatusBulkUpdateResult({id}))}

            sortComparator={(a, b) => a.accountName.localeCompare(b.accountName)}
        />
    );
};
